/* eslint-disable jsx-a11y/iframe-has-title */
import React, { useState, useEffect, forwardRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  Box,
  Flex,
  Input,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import DatePicker, { registerLocale } from 'react-datepicker';
import type { ReactDatePickerProps } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import moment from 'moment';
import { DataTable } from '../datatable';
import { INota, useNotas } from '../../hooks/useNotas';
import { IData, IPagination } from '../../types/shared';
import CreateNotaModal from '../createNota';
import { formatDate } from '../../utils/FormatFunctions';

import 'react-datepicker/dist/react-datepicker.css';

registerLocale('pt-BR', ptBR);

const CustomDateInput = forwardRef<HTMLInputElement, any>(({ value, onClick, ...props }, ref) => (
  <Input
    ref={ref}
    value={value}
    onClick={onClick}
    size="sm"
    w="120px"
    textAlign="center"
    readOnly
    cursor="pointer"
    {...props}
  />
));

const TypedDatePicker = DatePicker as unknown as React.ComponentType<ReactDatePickerProps>;

// Definição das abas disponíveis com seus labels
export const TABS_CONFIG = [
  { key: 'empresas', label: 'Empresas', title: 'Anotações de Empresas' },
  { key: 'faturamento', label: 'Faturamento', title: 'Anotações de Faturamento' },
  { key: 'simples_nacional', label: 'Simples Nacional', title: 'Anotações de Simples Nacional' },
  { key: 'fiscal', label: 'Fiscal', title: 'Anotações Fiscal' },
  { key: 'fgts_folha', label: 'Folha/FGTS', title: 'Anotações de Folha/FGTS' },
  { key: 'api_sieg', label: 'API Sieg', title: 'Anotações de API Sieg' },
  { key: 'quebra_sequencia', label: 'Quebra Sequência', title: 'Anotações de Quebra de Sequência' },
  { key: 'nfseDashboard', label: 'NFS-e', title: 'Anotações de NFS-e' },
] as const;

export type TabKey = (typeof TABS_CONFIG)[number]['key'];

// Helper para obter o label da aba
const getTabLabel = (key: TabKey): string => {
  return TABS_CONFIG.find(t => t.key === key)?.label || key;
};

// Helper para obter o título do modal
const getModalTitle = (key: TabKey): string => {
  return TABS_CONFIG.find(t => t.key === key)?.title || 'Anotações';
};

interface ViewNotaModalProps {
  id: any;
  dateStart: any;
  /** @deprecated Não é mais usado, o filtro é calculado a partir de dateStart */
  // eslint-disable-next-line react/no-unused-prop-types
  dateEnd?: any;
  children?: React.ReactNode;
  motivos?: string[];
  defaultTab?: TabKey;
  /** @deprecated Use defaultTab instead */
  screen?: string;
  companyName?: string;
}

const ViewNotaModal = ({
  id,
  dateStart,
  children,
  motivos,
  defaultTab,
  screen,
  companyName,
}: ViewNotaModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { getNotas } = useNotas();

  // Determina a aba inicial/permitida baseado em defaultTab ou screen (retrocompatibilidade)
  const getAllowedTab = (): TabKey => {
    if (defaultTab) return defaultTab;
    // Retrocompatibilidade: usa screen se for uma tab válida
    const validTab = TABS_CONFIG.find(t => t.key === screen);
    if (validTab) return validTab.key;
    return 'empresas';
  };

  const allowedTab = getAllowedTab();
  const [activeTab, setActiveTab] = useState<TabKey>(allowedTab);
  const [selectedDate, setSelectedDate] = useState<Date>(
    dateStart ? moment(dateStart, 'YYYY-MM-DD').toDate() : new Date(),
  );

  const filterDateStart = moment(selectedDate).startOf('month').format('YYYY-MM-DD');
  const filterDateEnd = moment(selectedDate).endOf('month').format('YYYY-MM-DD');

  // Reset tab e data quando o modal abre
  useEffect(() => {
    if (isOpen) {
      setActiveTab(allowedTab);
      setSelectedDate(dateStart ? moment(dateStart, 'YYYY-MM-DD').toDate() : new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultTab, screen, dateStart]);

  const columns: ColumnDef<INota>[] = [
    {
      accessorKey: 'usuario.nome',
      header: 'Usuário',
    },
    {
      accessorKey: 'dt_anotacao',
      header: 'Data',
      cell: ({ row }) => {
        const value = row.original.dt_anotacao;
        return formatDate(value);
      },
    },
    {
      accessorKey: 'descricao',
      header: 'Anotação',
    },
  ];

  // Encontra o índice da aba ativa
  const activeTabIndex = TABS_CONFIG.findIndex(t => t.key === activeTab);

  const handleTabChange = (index: number) => {
    setActiveTab(TABS_CONFIG[index].key);
  };

  // Componente para renderizar o conteúdo de cada aba
  const TabContent = ({ tabKey }: { tabKey: TabKey }) => {
    const filters = {
      page: '1',
      size: '10',
      empresaId: String(id),
      screen_data: tabKey,
      date_start: filterDateStart,
      date_end: filterDateEnd,
    };

    const {
      data: dataNotas,
      isFetching,
      refetch,
    } = useQuery<IData<INota[]>>({
      queryKey: ['Notas', filters],
      queryFn: getNotas,
      enabled: isOpen && activeTab === tabKey,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    });

    const { items: notas, ...pagination } = dataNotas?.data ?? {
      items: [],
      ...({} as IPagination),
    };

    const canCreate = tabKey === allowedTab;

    return (
      <>
        <div className="flex items-center justify-between pb-5">
          <div />
          <div className="flex">
            {canCreate ? (
              <CreateNotaModal
                screen={tabKey}
                id={id}
                dateStart={filterDateStart}
                handleReFecth={refetch}
                motivos={motivos}
              >
                <Button
                  variant="outline"
                  h="fit-content"
                  w="100%"
                  cursor="pointer"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  border="2px solid"
                  borderColor="#718EBF"
                  bg="white"
                  p={2}
                >
                  <Text className="text-sm" color="#718EBF">
                    Criar anotação
                  </Text>
                </Button>
              </CreateNotaModal>
            ) : null}
          </div>
        </div>

        {!canCreate && (
          <Alert status="info" mb={4} borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontSize="sm">
                Você está visualizando anotações de <strong>{getTabLabel(tabKey)}</strong>.
              </Text>
              <Text fontSize="sm" color="gray.600">
                Para criar anotações nesta categoria, acesse a página correspondente.
              </Text>
            </Box>
          </Alert>
        )}

        <div className="flex flex-col overflow-y-auto">
          <DataTable<INota>
            columns={columns}
            data={notas}
            pagination={pagination}
            emptyMsg="Nenhuma anotação encontrada."
            loadingMsg="Carregando anotações..."
            isFetching={isFetching}
          />
        </div>
      </>
    );
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpen();
  };

  return (
    <>
      {children ? (
        React.cloneElement(children as React.ReactElement, { onClick: handleOpen })
      ) : (
        <Button onClick={handleOpen}>Visualizar PDF</Button>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb={2}>
            {getModalTitle(allowedTab)}
            {companyName && ` - ${companyName}`}
          </ModalHeader>
          <ModalCloseButton />
          <Flex px={6} pb={4} gap={2} align="center">
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Período:
            </Text>
            <TypedDatePicker
              selected={selectedDate}
              onChange={(date: Date) => date && setSelectedDate(date)}
              locale="pt-BR"
              dateFormat="MM/yyyy"
              showMonthYearPicker
              customInput={<CustomDateInput />}
            />
          </Flex>
          <ModalBody p={0}>
            <Tabs index={activeTabIndex} onChange={handleTabChange} variant="unstyled">
              <TabList flexWrap="wrap" bg="gray.50" px={2} pt={2} gap={1} borderBottom="none">
                {TABS_CONFIG.map((tab, index) => {
                  const isActive = activeTabIndex === index;
                  const isAllowed = tab.key === allowedTab;

                  // Estilos baseados no estado - azul bebê para aba permitida
                  const getTabBg = () => {
                    if (!isActive) return 'white';
                    return isAllowed ? 'blue.50' : 'white';
                  };

                  const getTabColor = () => {
                    if (!isActive) return 'gray.600';
                    return isAllowed ? 'blue.700' : 'gray.700';
                  };

                  const getTabBorderColor = () => {
                    if (!isActive) return 'gray.200';
                    return isAllowed ? 'blue.200' : 'gray.300';
                  };

                  const getHoverBg = () => {
                    if (!isActive) return 'gray.100';
                    return isAllowed ? 'blue.200' : 'gray.50';
                  };

                  return (
                    <Tab
                      key={tab.key}
                      fontWeight="semibold"
                      fontSize="sm"
                      px={4}
                      py={2}
                      borderTopRadius="lg"
                      borderBottomRadius="none"
                      border="1px solid"
                      borderBottom="none"
                      mb="-1px"
                      transition="all 0.2s"
                      _selected={{
                        bg: isAllowed ? 'blue.50' : 'white',
                        color: isAllowed ? 'blue.700' : 'gray.700',
                        borderColor: isAllowed ? 'blue.200' : 'gray.300',
                        boxShadow: 'sm',
                      }}
                      bg={getTabBg()}
                      color={getTabColor()}
                      borderColor={getTabBorderColor()}
                      _hover={{ bg: getHoverBg() }}
                    >
                      {isAllowed && (
                        <Box as="span" w={2} h={2} borderRadius="full" bg="blue.500" mr={2} />
                      )}
                      {tab.label}
                    </Tab>
                  );
                })}
              </TabList>

              <TabPanels>
                {TABS_CONFIG.map(tab => {
                  const isAllowed = tab.key === allowedTab;
                  return (
                    <TabPanel
                      key={tab.key}
                      p={0}
                      bg={isAllowed ? 'blue.50' : 'white'}
                      border="1px solid"
                      borderColor={isAllowed ? 'blue.200' : 'gray.200'}
                      borderTop="none"
                      borderBottomRadius="lg"
                      minH="400px"
                    >
                      <Box p={4}>{activeTab === tab.key && <TabContent tabKey={tab.key} />}</Box>
                    </TabPanel>
                  );
                })}
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ViewNotaModal;
